import { Component, ElementRef, inject, OnDestroy, ViewChild, ViewEncapsulation } from '@angular/core';
import { DialogModule } from 'primeng/dialog';
import { FirebaseFirestoreService } from '../../../services/firebase.firestore.service';
import { ButtonModule } from 'primeng/button';
import { CommonModule } from '@angular/common';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { Message } from 'primeng/message';
import { FirebaseFunctionsService } from '../../../services/firebase.functions.service';
import { delay, map, Observable, Subject, takeUntil, tap } from 'rxjs';
import { Address, CartItem, DeliveryLocation, OrderDetails, PricingMetrics } from '../../../constants';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { PaymentsComponent } from '../payments/payments.component';
import { StepperModule } from 'primeng/stepper';
import { SelectModule } from 'primeng/select';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { PaymentMethod } from '@stripe/stripe-js';
import { EditItemComponent } from '../edit-item/edit-item.component';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { PanelModule } from 'primeng/panel';
import { TimelineModule } from 'primeng/timeline';
import { ImageModule } from 'primeng/image';
import { AccordionModule } from 'primeng/accordion';

type StringKeyArrayValue<T> = {
  [key: string]: T[];
};
@Component({
    selector: 'app-cart',
    imports: [
        CommonModule,
        ButtonModule,
        DialogModule,
        ToastModule,
        Message,
        PaymentsComponent,
        StepperModule,
        SelectModule,
        FloatLabelModule,
        InputTextModule,
        InputNumberModule,
        ReactiveFormsModule,
        ProgressSpinnerModule,
        PanelModule,
        TimelineModule,
        ImageModule,
        AccordionModule
    ],
    providers: [MessageService, FormBuilder, DialogService],
    templateUrl: './cart.component.html',
    styleUrl: './cart.component.scss',
    host: {
      class: 'app-cart'
    },
    encapsulation: ViewEncapsulation.None
})
export class CartComponent implements OnDestroy {
  firebaseFirestoreService = inject(FirebaseFirestoreService);
  firebaseFunctionsService = inject(FirebaseFunctionsService);
  messageService = inject(MessageService);
  dynamicDialogRef = inject(DynamicDialogRef);
  dialogService = inject(DialogService);
  formBuilder = inject(FormBuilder);
  cartItems = [] as CartItem[];
  storeCount = 0;
  Math = Math;

  destroy$ = new Subject();

  groupedCartItems$: Observable<StringKeyArrayValue<CartItem>> = this.firebaseFirestoreService.getActiveCartItems().pipe(
    takeUntil(this.destroy$.asObservable()),
    tap(items => {
      this.hasCartItems = items.length > 0;
      this.cartItems = items as CartItem[];
    }),
    // filter cart items from user history items
    tap((items:CartItem[]) => {
      this.filteredUserGroceryHistoryData = this.userGroceryHistoryData.filter(item => !items.some(i => i.id === item.id));
    }),
    map((items:CartItem[] ) => {
      return items.reduce((grouped, item) => {
        const key = item.store || '';
        if (!grouped[key]) {
          grouped[key] = [];
        }
        grouped[key].push(item);
        return grouped;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      }, {} as any);
    }),
    tap((groupedItems) => this.storeCount = Object.keys(groupedItems).length),
    delay(500)
  );

  step = 'view-cart';
  hasPaymentMethods = false;
  hasCartItems = true;
  preCheckoutChecksInProcessing = false;
  placingOrderInProgress = false;

  cities = [
    { name: 'Folsom', code: 'folsom' },
    { name: 'Rancho Cordova', code: 'rancho_cordova' },
  ]
  states = [
    { name: 'California', code: 'CA' }
  ]
  countries = [
    { name: 'US', code: 'USA' }
  ]
  pricingMetrics?: PricingMetrics;
  deliveryAddressformGroup?: FormGroup;
  tip = new FormControl(0);
  orderPricingMetrics = {
    shoppingFee: 0,
    multiStoreShoppingFee: 0,
    totalShoppingFee: 0,
    totalCartItems: 0,
    serviceFee: 0,
    salesTax: 0,
    deliveryFee: 0,
    totalDeliveryFee: 0,
    multiStoreDeliveryFee: 0,
    farApartStoresFee: 0,
    totalDistance: 0,
    totalTravelTime: 0,
    stores: [] as {
      name: string,
      allItemsMarked?: boolean,
      hasReceipt?: boolean,
      shoppingCompleted?: boolean,
    }[],
    storeCount: 1,
    storeLocations: [] as string[],
    totalFee: 0
  }
  paymentMethods = [] as PaymentMethod[];
  userGroceryHistoryData: CartItem[] = [];
  filteredUserGroceryHistoryData: CartItem[] = [];
  userPreviousAddresses: DeliveryLocation[] = [];
  selectedDeliveryAddress?: DeliveryLocation;
  showAddressForm = true;
  selectedPaymentMethod?: PaymentMethod;
  stepperValue = 1;
  checkoutSteps = [
      { name: 'address', title: 'Delivery Address', icon: 'pi pi-map-marker', completed: false },
      { name: 'payment', title: 'Payment Method', icon: 'pi pi-credit-card', completed: false },
      { name: 'list', title: 'Shopping List', icon: 'pi pi-shopping-cart', completed: true },
      { name: 'summary', title: 'Summary', icon: 'pi pi-receipt', completed: true },
  ];
  @ViewChild('paymentNextBtn',{read: ElementRef}) paymentNextBtn: ElementRef | null = null;
  @ViewChild('dialogContent') dialogContent!: ElementRef;

  get estimatedDeliveryTime(): string {
    // return `Today 6PM - 8PM`;
    const now = new Date();
    const hour = now.getHours();

    // if (hour < 10) { // Before 10AM
    //   return `Today 12PM - 2PM`;
    // } else 
    if (hour < 16) { // Before 4PM
      return `Today 6PM - 8PM`;
    } else { // After 4PM
      return `Tomorrow 6PM - 8PM`;
    }
  }

  constructor() {
    this.initAddressForm();
    this.getUserHistoryData();
    this.getUserPreviousAddresses();
  }

  ngOnDestroy() {
    this.destroy$.next(null);
  }

  initAddressForm() {
    this.deliveryAddressformGroup = this.formBuilder.group({
      streetAddress: ['', Validators.required],
      city: [undefined, Validators.required],
      zipcode: ['', [Validators.required, Validators.maxLength(5), Validators.minLength(5)]],
      state: [this.states[0]],
      country: [this.countries[0]],
    });
  }
  async saveAddress() {
    this.checkoutSteps[0].completed = !!this.deliveryAddressformGroup?.valid;
    this.checkoutSteps = [...this.checkoutSteps];
    if(this.deliveryAddressformGroup?.valid && this.deliveryAddressformGroup?.getRawValue().streetAddress) {
      this.selectedDeliveryAddress = {
        ...this.deliveryAddressformGroup?.getRawValue(),
        fullAddress: this.getDeliveryAddress()
      }
      const id = await this.firebaseFirestoreService.addToUserPreviousAddresses(this.selectedDeliveryAddress as DeliveryLocation);
      this.selectedDeliveryAddress = {
        ...this.deliveryAddressformGroup.getRawValue(),
        fullAddress: this.getDeliveryAddress(),
        uid: id
      }
      this.getUserPreviousAddresses();
      this.calculateRouteMetrics();
      this.initAddressForm();
    }
  }

  async deleteAddress(uid: string) {
    await this.firebaseFirestoreService.deleteUserPreviousAddress(uid);
    if(this.selectedDeliveryAddress?.uid === uid) {
      this.selectedDeliveryAddress = undefined;
      this.orderPricingMetrics = {
        ...this.orderPricingMetrics,
        ...this.firebaseFirestoreService.calculateOrderPricing(this.cartItems, this.pricingMetrics as PricingMetrics),
        farApartStoresFee: 0
      }
    }
    this.getUserPreviousAddresses();
  }

  async getUserHistoryData() {
    this.userGroceryHistoryData = await this.firebaseFirestoreService.getUserGroceryHistory();
    this.filteredUserGroceryHistoryData = this.userGroceryHistoryData.filter(item => !this.cartItems.some(i => i.id === item.id));
  }

  async getUserPreviousAddresses() {
    this.userPreviousAddresses = await this.firebaseFirestoreService.getUserPreviousAddresses();
    console.log(this.userPreviousAddresses)
    if(this.userPreviousAddresses.length > 0) {
      this.showAddressForm = false;
      this.selectedDeliveryAddress = this.selectedDeliveryAddress || this.userPreviousAddresses.find((address) => address.preferred) || this.userPreviousAddresses[0];
    } else {
      this.showAddressForm = true;
    }  
  }

  closeDialog() {
    this.dynamicDialogRef.close();
  }

  goBack() {
    this.step = 'view-cart';
  }

  addItemToCart() {
    this.dynamicDialogRef.close('addItem');
  }

  onGrocerySuggestionSelection(item: CartItem) {
    const { name, location = '', quantity = '', unit = '', store = '', images = [], notes = '', id = '' } = item
      this.firebaseFirestoreService.addItemToCart('from recent orders', {name, location, quantity, store, images, unit, notes, id });
  }

  isOpen = false;
  togglePanel() {
    const panel = document.getElementById('panel-content') as HTMLElement;
    const icon = document.getElementById('toggle-icon') as HTMLElement;

    this.isOpen = !this.isOpen;

    panel.style.maxHeight = this.isOpen ? panel.scrollHeight + 'px' : '0px';
    icon.style.transform = this.isOpen ? 'rotate(180deg)' : 'rotate(0deg)';
  }

  getOptionNameByCode(code: string) {
    return this.cities.find(c => c.code === code)?.name;
  }

  getDeliveryAddress() {
    const addressComponents = this.deliveryAddressformGroup?.getRawValue() as Address;
    return `${addressComponents?.streetAddress}, ${addressComponents?.city?.name}, ${addressComponents?.state?.name}, ${addressComponents?.zipcode}, ${addressComponents?.country?.name}`;
  }

  async delete(id: string) {
    const result = await this.firebaseFirestoreService.removeItemFromCart(id);
    if(result) {
      this.messageService.add({ severity: 'error', summary: 'Error', key: 'bl', detail: 'Failed to remove item from cart. Please try again', life: 3000 });
    }
  }
  edit(item: CartItem) {
    const editItemDialogRef = this.dialogService.open(EditItemComponent, {
      modal: true,
      closable: true,
      closeOnEscape: false,
      header: 'Edit Item',
      style: {
        'width': '100%',
        'max-width': '640px',
        'height': '100vh',
        'max-height': 'none',
        'border-radius': '0',
        'border': 'none'
      },
      data: item
    });
    editItemDialogRef.onClose.subscribe(() => {
      this.orderPricingMetrics = {
        ...this.orderPricingMetrics,
        ...this.firebaseFirestoreService.calculateOrderPricing(this.cartItems, this.pricingMetrics as PricingMetrics)
      };
      this.calculateRouteMetrics();
    });
  }

  async checkout() {
    this.preCheckoutChecksInProcessing = true;
    try {
      this.paymentMethods = await this.firebaseFunctionsService.getCustomerPaymentMethods(this.firebaseFirestoreService.getUserProfile());
      this.pricingMetrics = await this.firebaseFirestoreService.getPricingMetrics() as PricingMetrics;
      this.orderPricingMetrics = {
        ...this.orderPricingMetrics,
        ...this.firebaseFirestoreService.calculateOrderPricing(this.cartItems, this.pricingMetrics)
      }
      this.step = 'checkout';
      this.hasPaymentMethods = this.paymentMethods.length > 0;
      this.preCheckoutChecksInProcessing = false;
      this.selectedPaymentMethod = this.paymentMethods[0];

      // TODO: remove stepper code - we cganged to timeline view from stepper view
      if(this.hasPaymentMethods && this.selectedDeliveryAddress) {
        this.stepperValue = 4;
      } else if(this.selectedDeliveryAddress && !this.hasPaymentMethods) {
        this.stepperValue = 2;
      }
      this.checkoutSteps[0].completed = !!this.selectedDeliveryAddress;
      this.checkoutSteps[1].completed = this.hasPaymentMethods;
      this.checkoutSteps = [...this.checkoutSteps];

      this.calculateRouteMetrics();
      setTimeout(() => {
        this.dialogContent.nativeElement.scrollTop = 0;
      }, 0);
    } catch (error) {
      console.log(error);
      this.step = 'checkout';
    }  
  }

  routeMetrics: {
    totalDistanceMiles: number,
    extraFee: number,
    storeLocations: {name: string, address: string, lat: number, lng: number}[],
    deliveryLocation: {lat: number, lng: number},
    routeLegs: {startAddress: string, endAddress: string, distanceMiles: number, durationMins: number}[]
  } | null = null;
  calculateRouteMetrics() {
    if((this.selectedDeliveryAddress || this.deliveryAddressformGroup?.valid ) && this.orderPricingMetrics.stores.length > 0) {
        this.firebaseFunctionsService.calculateRouteMetrics(
          this.orderPricingMetrics.stores.map(store => store.name), 
          this.selectedDeliveryAddress?.lat && this.selectedDeliveryAddress?.lng ? 
            this.selectedDeliveryAddress :
            this.constructFullAddress(this.selectedDeliveryAddress || this.deliveryAddressformGroup?.getRawValue()),
          this.pricingMetrics?.deliveryFee.thresholdKm,
          this.pricingMetrics?.deliveryFee.perKm
        ).then((data) => {
          console.log('calculateRouteMetrics', data);
          this.routeMetrics = data as {
            totalDistanceMiles: number,
            extraFee: number,
            storeLocations: {name: string, address: string, lat: number, lng: number}[],
            deliveryLocation: {lat: number, lng: number},
            routeLegs: {startAddress: string, endAddress: string, distanceMiles: number, durationMins: number}[]
          };
          // recalculate pricing metrics
          this.orderPricingMetrics = {
            ...this.orderPricingMetrics,
            ...this.firebaseFirestoreService.calculateOrderPricing(this.cartItems, this.pricingMetrics as PricingMetrics)
          };
          // update route metrics
          this.orderPricingMetrics = {
            ...this.orderPricingMetrics,
            totalDistance: data.totalDistanceMiles,
            totalTravelTime: data.routeLegs.reduce((acc, leg) => acc + leg.durationMins, 0),
            farApartStoresFee: data.extraFee,
            totalFee: this.orderPricingMetrics.totalFee + data.extraFee
          }
          this.selectedDeliveryAddress = {
            ...this.selectedDeliveryAddress as DeliveryLocation,
            lat: this.routeMetrics.deliveryLocation.lat,
            lng: this.routeMetrics.deliveryLocation.lng
          }
        });
      }
  }

  calculateDeliveryRouteDetails() {
    if((this.selectedDeliveryAddress || this.deliveryAddressformGroup?.valid ) && this.orderPricingMetrics.stores.length > 0) {
        this.firebaseFunctionsService.calculateDeliveryRoute(this.orderPricingMetrics.stores.map(store => store.name), this.constructFullAddress(this.selectedDeliveryAddress || this.deliveryAddressformGroup?.getRawValue())).then((data) => {
          console.log(data)
        });
      }
  }

  onPaymentMethodChange(paymentMethod: PaymentMethod) {
    console.log('onPaymentMethodChange', paymentMethod);
    if(!paymentMethod) return;
    this.selectedPaymentMethod = this.paymentMethods.find(p => p.id === paymentMethod.id);
  }

  async onPaymentMethodAdded(paymentMethodId: string) {
    console.log('onPaymentMethodAdded', paymentMethodId)
    this.paymentMethods = await this.firebaseFunctionsService.getCustomerPaymentMethods(this.firebaseFirestoreService.getUserProfile());
    this.hasPaymentMethods = this.paymentMethods.length > 0;
    this.checkoutSteps[1].completed = this.hasPaymentMethods;
    this.checkoutSteps = [...this.checkoutSteps];
    this.selectedPaymentMethod = this.paymentMethods.find(p => p.id === paymentMethodId);
    // this.paymentNextBtn?.nativeElement.click();
  }

  async placeOrder() {
    if(!(this.selectedDeliveryAddress || this.deliveryAddressformGroup?.valid )|| !this.hasPaymentMethods || !this.hasCartItems) {
      return;
    }
    this.placingOrderInProgress = true;
    const ordersCount = await this.firebaseFirestoreService.getOrdersCount();
    
    // add to orders and remove items from active cart
    const orderDetails = {
      items: this.cartItems,
      orderStatus: 'order placed',
      orderNumber: `GON${Math.floor(100000 + Math.random() * 900000)}-${ordersCount + 1}`,
      estimatedDeliveryWindow: this.estimatedDeliveryTime,
      estimatedDeliveryDateTime: new Date().valueOf(),
      estimatedDeliveryDateTimeString: new Date().toLocaleString(),
      PaymentStatus: 'pending',
      tip:  Number(this.tip.value),
      ...this.orderPricingMetrics,
      deliveryLocation: this.selectedDeliveryAddress || {
        ...this.deliveryAddressformGroup?.getRawValue(),
        fullAddress: this.constructFullAddress(this.deliveryAddressformGroup?.getRawValue()),
        ...this.routeMetrics?.deliveryLocation
      },
      storeAddresses: this.routeMetrics?.storeLocations,
      paymentMethodDetails: this.selectedPaymentMethod || this.paymentMethods[0],
      paymentMethodId: this.selectedPaymentMethod?.id || this.paymentMethods[0].id,
      pricingMetricsUsed: this.pricingMetrics,
      routeMetrics: this.routeMetrics
    } as OrderDetails
    console.log(orderDetails);
    const newOrderDetails = await this.firebaseFirestoreService.addToOrders(orderDetails);
    console.log(newOrderDetails);
    if(!newOrderDetails) {
      // show error message
      return;
    }
    this.placingOrderInProgress = false;
    this.firebaseFirestoreService.addToOpenOrders(newOrderDetails);
    this.firebaseFirestoreService.emptyCart();
    this.dynamicDialogRef.close(newOrderDetails);          
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructFullAddress(address: any) {
    return `${address.streetAddress || ''}, ${address.city?.name || ''}, ${address.state?.name || ''}, ${address.country?.name || ''}, ${address.zipcode || ''}` 
  }

}
